import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import {
  Clock,
  ChevronRight,
} from "@/components/icons";
import StatCard from "../../components/StatCard";
import AppointmentsTable from "../../components/AppointmentsTable";

export default function SecretaryOverview() {
  const [stats, setStats] = useState(null);
  const [appts, setAppts] = useState([]);
  const [pays, setPays] = useState([]);
  const [patients, setPatients] = useState([]);

  const reloadAppts = () => {
    api.get("/appointments").then((r) => setAppts(r.data));
  };

  useEffect(() => {
    api.get("/doctor/stats").then((r) => setStats(r.data));
    api.get("/appointments").then((r) => setAppts(r.data));
    api.get("/payments").then((r) => setPays(r.data));
    api.get("/patients").then((r) => setPatients(r.data));
  }, []);

  // Filter upcoming for use below
  const upcoming = appts
    .filter((a) => a.date.slice(0, 10) > (new Date().toISOString().slice(0, 10)))
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 8);

  if (!stats) return <div className="text-black">Cargando…</div>;
  const _now = new Date();
  const today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}-${String(_now.getDate()).padStart(2, "0")}`;
  const todayAppts = appts.filter((a) => a.date.slice(0, 10) === today);
  const todayRevenue = pays
    .filter((p) => (p.status || "paid") === "paid" && p.paid_at?.slice(0, 10) === today)
    .reduce((acc, p) => acc + p.amount, 0);
  const pending = pays.filter((p) => p.status === "pending");
  const patientById = Object.fromEntries(patients.map((p) => [p.id, p]));

  // Split today's appointments: in_progress vs scheduled-or-other.
  // Strict ascending sort by HH:mm so the next upcoming is always at top.
  const byTimeAsc = (a, b) => a.date.slice(11, 16).localeCompare(b.date.slice(11, 16));
  const inProgressToday = todayAppts
    .filter((a) => a.status === "in_progress")
    .sort(byTimeAsc);
  const upcomingTodayRest = todayAppts
    .filter((a) => a.status !== "in_progress")
    .sort(byTimeAsc);

  return (
    <div className="space-y-6" data-testid="secretary-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
        <StatCard
          title="Citas hoy"
          value={stats.today_appointments}
          subtitle={`${todayAppts.length} programada${todayAppts.length === 1 ? "" : "s"}`}
          to="/secretaria/agenda"
          testid="stat-today-appts"
        />
        <StatCard
          title="Pacientes"
          value={stats.total_patients}
          subtitle="Registrados"
          to="/secretaria/pacientes"
          testid="stat-patients"
        />
        <StatCard
          title="Cobrado hoy"
          value={`$${todayRevenue.toFixed(2)}`}
          subtitle="Ingresos del día"
          to="/secretaria/cobros"
          testid="stat-revenue-today"
        />
        <StatCard
          title="Pagos pendientes"
          value={pending.length}
          subtitle="Por cobrar"
          to="/secretaria/cobros"
          active={pending.length > 0}
          testid="stat-pending"
        />
      </div>

      {/* Pagos pendientes — solo se muestra cuando hay pendientes */}
      {pending.length > 0 && (
        <div className="bg-white rounded-[28px] border border-black/[0.06] p-6 relative">
          <Link
            to="/secretaria/cobros"
            className="absolute top-5 right-6 text-xs font-bold text-black/55 hover:text-black inline-flex items-center"
          >
            Ver todos <ChevronRight className="w-4 h-4" />
          </Link>
          <div className="flex items-center gap-3 mb-5">
            <Clock className="w-6 h-6 text-amber-500" />
            <div>
              <div className="text-lg font-extrabold text-black">
                Pagos pendientes a cobrar
              </div>
              <div className="text-xs text-black/55">
                Consultas finalizadas por el doctor que aún no se han cobrado.
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pending-payments-table">
              <thead>
                <tr className="text-left">
                  {["Fecha", "Paciente", "Concepto", ""].map((h) => (
                    <th
                      key={h}
                      className="py-3 px-3 first:pl-0 last:pr-0 text-[11px] uppercase tracking-widest text-[#9CA3AF] font-medium"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pending.slice(0, 6).map((p) => {
                  const pat = patientById[p.patient_id];
                  return (
                    <tr
                      key={p.id}
                      data-testid={`pending-row-${p.id}`}
                      className="border-t border-[#E5E7EB] text-black hover:bg-black/[0.03] transition"
                    >
                      <td className="py-3 pr-3 first:pl-0 last:pr-0 whitespace-nowrap font-bold">
                        {new Date(p.created_at).toLocaleDateString("es-ES")}
                      </td>
                      <td className="py-3 px-3 font-bold">
                        {pat?.full_name || "Paciente"}
                      </td>
                      <td className="py-3 px-3 text-black/80 truncate max-w-[420px]">
                        {p.concept || "Consulta"}
                      </td>
                      <td className="py-3 pl-3 first:pl-0 last:pr-0 text-right">
                        <Link
                          to={`/secretaria/cobros?collect=${p.id}`}
                          data-testid={`collect-${p.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#01BC8E] text-white text-xs font-bold hover:bg-[#019A75] transition"
                        >
                          Cobrar
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Próximas citas de hoy */}
      <div className="bg-white rounded-[28px] border border-black/[0.06] p-6 relative">
        <Link
          to="/secretaria/agenda"
          className="absolute top-5 right-6 text-xs font-bold text-black/55 hover:text-black"
        >
          Ver agenda →
        </Link>
        <div className="text-center mb-5">
          <div className="text-lg font-extrabold text-black">
            Próximas citas de hoy
          </div>
          <div className="text-xs text-black/55">
            {upcomingTodayRest.length === 0
              ? "Sin próximas citas para hoy"
              : `${upcomingTodayRest.length} cita${upcomingTodayRest.length === 1 ? "" : "s"} programada${upcomingTodayRest.length === 1 ? "" : "s"}`}
          </div>
        </div>
        <AppointmentsTable
          appointments={upcomingTodayRest}
          patientById={patientById}
          showIniciar={false}
          showArrived={true}
          onChanged={reloadAppts}
          testid="today-appts-table"
          emptyText="Sin próximas citas para hoy."
        />
      </div>

      {/* Consultas en curso — doctor ya las inició pero aún no las cerró */}
      {inProgressToday.length > 0 && (
        <div className="bg-white rounded-[28px] border border-black/[0.06] p-6 relative">
          <Link
            to="/secretaria/agenda"
            className="absolute top-5 right-6 text-xs font-bold text-black/55 hover:text-black"
          >
            Ver agenda →
          </Link>
          <div className="text-center mb-5">
            <div className="text-lg font-extrabold text-black flex items-center justify-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              Consultas en curso
            </div>
            <div className="text-xs text-black/55">
              {inProgressToday.length} pendiente{inProgressToday.length === 1 ? "" : "s"} de finalizar
            </div>
          </div>
          <AppointmentsTable
            appointments={inProgressToday}
            patientById={patientById}
            showIniciar={false}
            showArrived={true}
            onChanged={reloadAppts}
            testid="in-progress-appts-table"
            emptyText="Sin consultas en curso."
          />
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="bg-white rounded-[28px] border border-black/[0.06] p-6 relative">
          <Link
            to="/secretaria/agenda"
            className="absolute top-5 right-6 text-xs font-bold text-black/55 hover:text-black"
          >
            Ver agenda →
          </Link>
          <div className="text-center mb-5">
            <div className="text-lg font-extrabold text-black">
              Próximas citas
            </div>
            <div className="text-xs text-black/55">
              {upcoming.length} próxima{upcoming.length === 1 ? "" : "s"}
            </div>
          </div>
          <AppointmentsTable
            appointments={upcoming}
            patientById={patientById}
            showIniciar={false}
            onChanged={reloadAppts}
            testid="upcoming-appts-table"
            emptyText="No hay próximas citas."
          />
        </div>
      )}
    </div>
  );
}
